import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter, faCircleInfo, faXmark, faMinus, faPlus } from "@fortawesome/free-solid-svg-icons";

const OPERATORS = [
  { label: "Equals", value: "equals" },
  { label: "Greater than", value: "greater_than" },
  { label: "Less than", value: "less_than" },
  { label: "Between", value: "between" },
];

const emptyCondition = () => ({ id: Date.now(), operator: "equals", value: "", min: "", max: "" });

function formatConditionLabel(c) {
  if (c.operator === "between") return `Amount: ${c.min || "?"} – ${c.max || "?"}`;
  if (c.operator === "equals") return `Amount = ${c.value}`;
  if (c.operator === "greater_than") return `Amount > ${c.value}`;
  if (c.operator === "less_than") return `Amount < ${c.value}`;
  return null;
}

function conditionHasValue(c) {
  if (c.operator === "between") return c.min !== "" || c.max !== "";
  return c.value !== "";
}

/**
 * DonationFilterDrawer
 *
 * Props:
 *   isOpen        bool
 *   onClose       () => void
 *   campaignName  string
 *   appliedFilters  { conditions: [] }
 *   onApply       (filters) => void
 */
const DonationFilterDrawer = ({ isOpen, onClose, campaignName, appliedFilters, onApply }) => {
  const [conditions, setConditions] = useState([emptyCondition()]);
  const [amountExpanded, setAmountExpanded] = useState(true);

  useEffect(() => {
    if (isOpen) {
      const saved = appliedFilters?.conditions;
      setConditions(saved?.length ? saved : [emptyCondition()]);
    }
  }, [isOpen]);

  const addCondition = () => setConditions((prev) => [...prev, emptyCondition()]);

  const removeCondition = (id) => {
    setConditions((prev) => {
      const next = prev.filter((c) => c.id !== id);
      return next.length ? next : [emptyCondition()];
    });
  };

  const updateCondition = (id, field, val) => {
    setConditions((prev) =>
      prev.map((c) => (c.id === id ? { ...c, [field]: val } : c))
    );
  };

  const clearAll = () => setConditions([emptyCondition()]);

  const activeConditions = conditions.filter(conditionHasValue);

  const handleApply = () => {
    onApply({ conditions });
    onClose();
  };

  const handleCancel = () => {
    const saved = appliedFilters?.conditions;
    setConditions(saved?.length ? saved : [emptyCondition()]);
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/30 z-40 transition-opacity duration-300 ${
          isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={handleCancel}
      />

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-[420px] bg-white shadow-2xl z-50 flex flex-col transform transition-transform duration-300 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-col flex-1 overflow-y-auto p-8 gap-5">
          {/* Campaign badge */}
          {campaignName && (
            <span className="self-start bg-orange text-white text-xs font-semibold px-3 py-1.5 rounded-full">
              {campaignName}
            </span>
          )}

          <h2 className="!font-poppins !font-bold !text-[24px] !text-gray-900 !m-0">
            Filters
          </h2>

          {/* Active filters */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="!text-sm !font-semibold text-gray-700">Active filters</span>
              {activeConditions.length > 0 && (
                <button
                  onClick={clearAll}
                  className="flex items-center gap-1.5 border border-gray-300 rounded-full px-3 py-1 !text-xs text-gray-600 hover:bg-gray-50"
                >
                  <FontAwesomeIcon icon={faFilter} size="xs" />
                  Clear all filters
                </button>
              )}
            </div>

            {activeConditions.length === 0 ? (
              <p className="!text-sm text-gray-400">No active filters.</p>
            ) : (
              <div className="flex flex-col gap-2">
                {activeConditions.map((c) => (
                  <div key={c.id} className="flex items-center gap-2 text-sm text-gray-700">
                    <FontAwesomeIcon icon={faCircleInfo} className="text-gray-400" size="sm" />
                    <span>
                      <span className="font-medium">Filter operator:</span>{" "}
                      {formatConditionLabel(c)}
                    </span>
                    <button
                      onClick={() => removeCondition(c.id)}
                      className="ml-auto text-gray-400 hover:text-gray-600 bg-transparent border-none outline-none"
                    >
                      <FontAwesomeIcon icon={faXmark} size="sm" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <hr className="border-gray-200" />

          {/* Amount section */}
          <div>
            <button
              onClick={() => setAmountExpanded((v) => !v)}
              className="w-full flex items-center justify-between bg-transparent border-none outline-none mb-3"
            >
              <span className="!text-sm !font-semibold text-gray-800">Amount</span>
              <FontAwesomeIcon icon={faMinus} size="sm" className="text-gray-500" />
            </button>

            {amountExpanded && (
              <div className="flex flex-col gap-3">
                {conditions.map((c, index) => (
                  <div key={c.id} className="flex flex-col gap-2">
                    {/* Operator dropdown */}
                    <div className="relative">
                      <select
                        value={c.operator}
                        onChange={(e) => updateCondition(c.id, "operator", e.target.value)}
                        className="w-full border border-gray-300 rounded-full px-4 py-2.5 text-sm text-gray-700 bg-white appearance-none outline-none focus:border-blue cursor-pointer pr-8"
                      >
                        {OPERATORS.map((op) => (
                          <option key={op.value} value={op.value}>
                            {op.label}
                          </option>
                        ))}
                      </select>
                      <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-xs">▾</span>
                    </div>

                    {/* Value input(s) */}
                    {c.operator === "between" ? (
                      <div className="flex items-center gap-3">
                        <input
                          type="number"
                          placeholder="Min"
                          value={c.min}
                          onChange={(e) => updateCondition(c.id, "min", e.target.value)}
                          className="flex-1 border border-gray-300 rounded-full px-4 py-2.5 text-sm outline-none focus:border-blue"
                        />
                        <span className="text-gray-400">—</span>
                        <input
                          type="number"
                          placeholder="Max"
                          value={c.max}
                          onChange={(e) => updateCondition(c.id, "max", e.target.value)}
                          className="flex-1 border border-gray-300 rounded-full px-4 py-2.5 text-sm outline-none focus:border-blue"
                        />
                      </div>
                    ) : (
                      <input
                        type="number"
                        placeholder="Value"
                        value={c.value}
                        onChange={(e) => updateCondition(c.id, "value", e.target.value)}
                        className="w-full border border-gray-300 rounded-full px-4 py-2.5 text-sm outline-none focus:border-blue"
                      />
                    )}
                  </div>
                ))}

                {/* Add condition */}
                <button
                  onClick={addCondition}
                  className="flex items-center gap-1.5 !text-sm text-gray-500 hover:text-gray-700 bg-transparent border-none outline-none self-start mt-1"
                >
                  <FontAwesomeIcon icon={faPlus} size="xs" />
                  Add
                </button>
              </div>
            )}
          </div>

          <hr className="border-gray-200" />
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex gap-4">
          <button
            onClick={handleCancel}
            className="flex-1 py-2.5 rounded-full border border-gray-300 !text-sm text-gray-700 bg-white hover:bg-gray-50 font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            className="flex-1 py-2.5 rounded-full bg-blue !text-sm text-white font-semibold hover:opacity-90 transition-opacity"
          >
            Apply
          </button>
        </div>
      </div>
    </>
  );
};

export default DonationFilterDrawer;
